import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GeoRegionOption } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

export function ModelGeoBlocksCard(): ReactElement {
  const { api } = useAuth();
  const [regions, setRegions] = useState<GeoRegionOption[]>([]);
  const [blockedCountryCodes, setBlockedCountryCodes] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    const [regionsResult, blocksResult] = await Promise.all([
      api.getGeoRegions(),
      api.getModelGeoBlocks(),
    ]);
    setRegions(regionsResult.regions);
    setBlockedCountryCodes(blocksResult.blockedCountryCodes);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.geoBlocks.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  const filteredRegions = useMemo(() => {
    const query = filter.trim().toLowerCase();
    const sorted = [...regions].sort((a, b) => a.name.localeCompare(b.name));
    if (!query) {
      return sorted;
    }
    return sorted.filter(
      (region) =>
        region.name.toLowerCase().includes(query) ||
        region.countryCode.toLowerCase().includes(query),
    );
  }, [regions, filter]);

  function toggleCountry(countryCode: string): void {
    setSaved(false);
    setBlockedCountryCodes((current) =>
      current.includes(countryCode)
        ? current.filter((code) => code !== countryCode)
        : [...current, countryCode],
    );
  }

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await api.setModelGeoBlocks({ blockedCountryCodes });
      setBlockedCountryCodes(result.blockedCountryCodes);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.geoBlocks.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.geoBlocks.loading')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.geoBlocks.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.geoBlocks.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
        {t('common.studio.geoBlocks.blockedCount').replace('{{count}}', String(blockedCountryCodes.length))}
      </p>

      {regions.length === 0 && !error ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.geoBlocks.emptyRegions')}
        </p>
      ) : null}
      {regions.length > 0 ? (
        <>
          <input
            className="fm-input"
            type="search"
            value={filter}
            disabled={isSaving}
            placeholder={t('common.studio.geoBlocks.searchPlaceholder')}
            aria-label={t('common.studio.geoBlocks.searchPlaceholder')}
            onChange={(event) => {
              setFilter(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginBottom: '0.75rem' }}
          />

          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 1rem',
              maxHeight: '16rem',
              overflowY: 'auto',
              border: '1px solid var(--fm-color-border)',
              borderRadius: 'var(--fm-radius-sm, 4px)',
            }}
          >
            {filteredRegions.map((region) => (
              <li
                key={region.countryCode}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderBottom: '1px solid var(--fm-color-border)',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                    cursor: isSaving ? 'wait' : 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={blockedCountryCodes.includes(region.countryCode)}
                    disabled={isSaving}
                    onChange={() => {
                      toggleCountry(region.countryCode);
                    }}
                  />
                  <span>
                    {region.name}{' '}
                    <span style={{ color: 'var(--fm-color-text-muted)' }}>({region.countryCode})</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {saved ? (
        <p style={{ margin: '0 0 0.75rem', color: 'var(--fm-color-link)', fontSize: '0.875rem' }}>
          {t('common.studio.geoBlocks.saved')}
        </p>
      ) : null}

      <Button
        label={isSaving ? t('common.studio.geoBlocks.saving') : t('common.studio.geoBlocks.save')}
        disabled={isSaving || regions.length === 0}
        onClick={() => {
          void handleSave();
        }}
      />
    </div>
  );
}
